<template>
  <BasePage data-test-id="peopleView">
    <DataTable
      show-expand
      :protected-item-ids="protectedItemIds"
      :disable-delete-message="t('warnings.deleteProtectedPerson')"
      :edit-button-title="t('buttons.viewDetails')"
      :search-type="SearchType.Query"
      :table-store-id="TableStoreId.People"
      :delete-message="deleteMessage"
      :note-title="noteTitle"
      :delete-title="t('dialogs.headers.deletePerson')"
      @new="createNewItem"
      v-model:expanded="store.expandedItems"
      @update:expanded="loadFitTestsByPerson"
      @update:options="onOptionsUpdated"
    >
      <template v-slot:left>
        <EditColumns
          :table-store-id="TableStoreId.People"
          :headers="store.getHeaders(true)"
          @save="onColumnsSaved"
        />
        <ButtonIcon
          :disabled="store.advancedFilterApplied === false"
          @click="store.resetAdvancedFilter()"
          :text="t('buttons.resetFilters')"
          icon="mdi-close-circle"
        />
      </template>
      <template
        v-for="(header, index) in store.getHeaders(true)"
        v-slot:[`header.${header.key}`]
        :key="index"
      >
        <HeaderWithFilter
          :header="header"
          :current-sort="currentSort"
          :table-store-id="TableStoreId.People"
        />
      </template>
      <template v-slot:item.email="{ item }">
        <v-icon
          v-if="item.email"
          v-tooltip="{ openOnClick: true, text: item.email }"
          size="small"
          @click="copyToClipboard(item)"
        >
          mdi-email
        </v-icon>
        <v-icon
          v-else
          v-tooltip="{ openOnClick: true, text: t('warnings.missingEmail') }"
          @click="store.editItem(item)"
          :color="Colors.accentWarning"
          size="small"
        >
          mdi-alert
        </v-icon>
      </template>

      <template v-slot:item.testDueDate="{ item, value }">
        <StatusChip
          :msg-no-record="t('labels.noPassingRecord')"
          :disabled="!item.respiratorRequired"
          :iso-date="value"
          :scheduled="item.testAppointments > 0"
          :days-until-due="settingsStore.globals.testing.daysUntilDue"
          :expires="true"
        />
      </template>

      <template
        v-if="settingsStore.globals.training.enabled"
        v-slot:item.trainingExpiration="{ item, value }"
      >
        <StatusChip
          :disabled="!item.respiratorRequired"
          :iso-date="value"
          :scheduled="item.trainingAppointments > 0"
          :days-until-due="settingsStore.globals.training.daysUntilDue"
          :expires="settingsStore.globals.training.expires"
        />
      </template>

      <template
        v-if="settingsStore.globals.clearance.enabled"
        v-slot:item.clearanceExpiration="{ item, value }"
      >
        <StatusChip
          :disabled="!item.respiratorRequired"
          :iso-date="value"
          :scheduled="item.clearanceAppointments > 0"
          :days-until-due="settingsStore.globals.clearance.daysUntilDue"
          :expires="settingsStore.globals.clearance.expires"
          :msg-compliant="t('labels.cleared')"
          :msg-no-record="t('labels.notCleared')"
        />
      </template>

      <template v-slot:item.note="{ item }">
        <v-icon v-if="(item as PeopleRecord).note" size="small" @click="showNote(item)">
          mdi-note-text
        </v-icon>
        <span style="margin-left: 0.38rem" v-else>-</span>
      </template>

      <template v-slot:expanded-row="{ item }">
        <tr class="expanded-row">
          <td class="expanded-column" :colspan="store.getHeaders().length + 1">
            <FitTestHistory :person="item" />
          </td>
        </tr>
      </template>

      <template v-slot:footer>
        <ManagePerson />
        <EditFitTest />
      </template>
    </DataTable>
  </BasePage>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { MessageType } from '@/enums/MessageType'
import { SearchType } from '@/enums/SearchType'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IFitTestsStore } from '@/interfaces/api/IFitTestsStore'
import type { IPeopleStore } from '@/interfaces/api/IPeopleStore'
import type { ITableOptions } from '@/interfaces/ITableOptions'
import type { ITableSort } from '@/interfaces/ITableSort'
import { PeopleRecord } from '@/models/PeopleRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { useSnackbarStore } from '@/stores/ui/SnackbarStore'
import Colors from '@/styles/_colors.module.scss'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

const store = TableStoreFactory.get(TableStoreId.People) as IPeopleStore
const fitTestStore = TableStoreFactory.get(TableStoreId.FitTests) as IFitTestsStore
const settingsStore = useSettingsStore()
const snackbarStore = useSnackbarStore()

// PROPS & EMITS

// REACTIVE VARIABLES
const currentSort = ref<ITableSort>()

// COMPUTED PROPERTIES
const deleteMessage = computed((): string => {
  if (store.selectedItem?.firstName && store.selectedItem?.lastName) {
    return t('dialogs.delete.personWarning', {
      name: `${store.selectedItem.firstName} ${store.selectedItem.lastName}`
    })
  }
  return ''
})

const noteTitle = computed((): string => {
  let title = t('dialogs.headers.showNotePerson')
  if (store.selectedItem) {
    title += ` ${store.selectedItem.firstName} ${store.selectedItem.lastName}:`
  }
  return title
})

const protectedItemIds = computed(() => {
  return store.items
    .filter((item: PeopleRecord) => {
      return item.totalDirectReports > 0
    })
    .map((item) => {
      return item.id
    })
})

// LIFECYCLE HOOKS

// FUNCTIONS
const copyToClipboard = (item: PeopleRecord) => {
  if (item?.email) {
    navigator.clipboard.writeText(item.email)
    snackbarStore.addMessage(t('text.copiedToClipboard'), MessageType.Info)
  }
}

const createNewItem = () => {
  store.createNewItem(new PeopleRecord())
}

const loadFitTestsByPerson = () => {
  if (store.expandedItems.length > 1) {
    store.expandedItems.shift()
  }

  if (store.expandedItems[0]) {
    const item = store.items.find((item) => {
      return item.id === store.expandedItems[0].id
    })

    if (item.id) {
      fitTestStore.setFitTestFilterByPerson(item)
    }
  }
}

const onColumnsSaved = () => {
  store.updateHeaders()
}

const onOptionsUpdated = (tableOptions: ITableOptions) => {
  currentSort.value = tableOptions.sortBy[0]
}

const showNote = (item: PeopleRecord) => {
  if (item.note) {
    let title = t('dialogs.headers.showNotePerson')
    title += ` ${item.firstName} ${item.lastName}:`
    store.showInfo(title, item.note)
  }
}
</script>
